import type { SyncState } from '@kit/sync/types/sync-state';
import { t } from '@/shared/i18n/t';
import { tn } from '@/shared/i18n/tn';
import { formatTimestamp } from '@/shared/lib/dates/format-timestamp';

type SyncDescription = {
  readonly label: string;
  readonly detail: string;
};

/** Full sentences joined with a space; each one is translated on its own. */
function sentences(...parts: readonly string[]): string {
  return parts.filter(Boolean).join(' ');
}

export function describeSync(state: SyncState, pending: number): SyncDescription {
  const last = state.lastSyncAt
    ? t('Last synced: {time}.', { time: formatTimestamp(state.lastSyncAt) })
    : t('Not synced yet.');
  const queued = pending > 0 ? tn(pending, '{count} change to send.', '{count} changes to send.') : '';
  switch (state.status) {
    case 'syncing':
      return { label: t('Syncing…'), detail: sentences(queued, last) };
    case 'offline':
      return {
        label: pending > 0 ? t('Offline · {count}', { count: pending }) : t('Offline'),
        detail: sentences(
          queued,
          t('Everything is saved on this device and will be sent when the connection is back.'),
          last,
        ),
      };
    case 'unauthorized':
      return {
        label: t('Sign in again'),
        detail: sentences(t('Your session has expired.'), queued, t('Your changes are safe on this device.')),
      };
    case 'error':
      return { label: t('Sync error'), detail: sentences(`${state.lastError ?? ''}.`, queued, last) };
    default:
      return {
        label: pending > 0 ? t('{count} to send', { count: pending }) : t('Synced'),
        detail: sentences(queued, last),
      };
  }
}
